/**
 * Content bridge — DB-first with mockData.js fallback per content_type.
 *
 * Behavior (per user directive from Prompt #3 Q4):
 *   1. On page load, fetch `GET /api/content/coverage/summary` — returns
 *      `{ meditation: N, affirmation: N, sleep_meditation: N, … }`.
 *   2. If coverage[type] > 0 → use only DB records for that type.
 *   3. If coverage[type] === 0 or missing → return the existing mockData.js
 *      items for that type unchanged (temporary bridge).
 *
 * Rules baked in:
 *   • Never merges mock + DB for the same type (no duplicates).
 *   • Fallback layer is a single isolated hook — remove or ignore its output
 *     once every content_type shows > 0 coverage and this file can be
 *     deleted without touching any consumer component.
 */
import { useEffect, useState } from 'react';
import { posts as MOCK_POSTS } from '../data/mockData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map DB content_type → the mockData.js posts key that holds equivalent
// fallback content. Only these mappings are supported; adding a new type
// here means both a DB seed and a mock category can coexist during
// migration.
const TYPE_TO_MOCK_CATEGORY = {
  meditation:       'guided-meditations',
  affirmation:      'affirmations',
  sleep_meditation: 'sleep',
  reflection:       'anxiety',
};

let _coverageCache = null;
let _coveragePromise = null;

/** Force a re-fetch — call after admin publishes new content. */
export const refreshCoverage = () => { _coverageCache = null; _coveragePromise = null; };

const fetchCoverage = () => {
  if (_coverageCache) return Promise.resolve(_coverageCache);
  if (_coveragePromise) return _coveragePromise;
  _coveragePromise = fetch(`${API_URL}/api/content/coverage/summary`)
    .then((r) => (r.ok ? r.json() : {}))
    .then((data) => { _coverageCache = data || {}; return _coverageCache; })
    .catch(() => { _coverageCache = {}; return _coverageCache; });
  return _coveragePromise;
};

/**
 * useContentByType(contentType) → { items, loading, source }
 *   • source is 'db' or 'mock' so debug UIs can show which layer served.
 *   • items shape matches the existing PostCard consumers (title, image,
 *     excerpt, duration, id, category, youtubeId) — DB records are mapped
 *     into that shape so no consumer component needs to change.
 */
export const useContentByType = (contentType) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('mock');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const coverage = await fetchCoverage();
      const hasDb = (coverage[contentType] || 0) > 0;
      if (hasDb) {
        try {
          const r = await fetch(`${API_URL}/api/content?type=${encodeURIComponent(contentType)}&limit=100`);
          const body = await r.json();
          if (cancelled) return;
          setItems((body.items || []).map(shapeDbToPost));
          setSource('db');
          setLoading(false);
          return;
        } catch { /* fall through to mock */ }
      }
      if (cancelled) return;
      const mockKey = TYPE_TO_MOCK_CATEGORY[contentType];
      setItems(mockKey ? (MOCK_POSTS[mockKey] || []) : []);
      setSource('mock');
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [contentType]);

  return { items, loading, source };
};

// DB → post-card shape. Keys line up with PostCard / CategoryPage / HomePage
// so nothing downstream has to change (Rule 53).
export const shapeDbToPost = (c) => ({
  id: c.id,
  title: c.title,
  date: c.publishedAt ? new Date(c.publishedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '',
  comments: 0,
  image: c.coverImageUrl || null,
  youtubeId: null,
  excerpt: c.shortDescription || (c.description || '').slice(0, 160),
  content: c.description || '',
  category: c.category || c.type,
  duration: c.durationSeconds ? `${Math.max(1, Math.round(c.durationSeconds / 60))} min` : undefined,
  tags: c.tags || [],
  moods: c.moods || [],
  audioUrl: c.audioAssetId ? `${API_URL}/api/media/${c.audioAssetId}` : (c.audioUrl || null),
  videoUrl: c.videoAssetId ? `${API_URL}/api/media/${c.videoAssetId}` : (c.videoUrl || null),
  type: c.type,
  _source: 'db',
});
